import React from 'react';
import { Plus, Crown, Zap, Check, FileText, Clock, ChevronDown, ChevronRight, FolderPlus } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Toggle } from "@/components/ui/toggle";
import { useQuery } from 'convex/react';
import { api } from '../../lib/convex';
import { useToast } from "@/components/ui/use-toast";
import { CONTENT_TYPES } from "../../../convex/constants/contentTypes";
import { SERVICE_TIERS, getTierFeatures, isAnthropicModel } from "../../../convex/constants/serviceTiers";
import { Id } from "../../../convex/_generated/dataModel";
import { TONES } from "../../../convex/constants/tones";
import { FRAMEWORKS } from "../../../convex/constants/frameworks";
import { WRITING_STYLES } from "../../../convex/constants/writingStyles";
import { READABILITY_LEVELS } from "../../../convex/constants/readabilityLevels";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

interface StandardOptionsProps {
  register: any;
  errors: any;
  setValue: any;
  handleWritingStyleChange: (value: string) => void;
  writingStyleState: 'select' | 'custom';
  customStylePrompt: string;
  setCustomStylePrompt: (value: string) => void;
  writingStyles: any[];
  selectedModel: string;
  setSelectedModel: (value: string) => void;
  availableModels: string[];
  tier: keyof typeof SERVICE_TIERS;
  tierFeatures: typeof SERVICE_TIERS[keyof typeof SERVICE_TIERS];
}

export default function StandardOptions({
  register,
  errors,
  setValue,
  handleWritingStyleChange,
  writingStyleState,
  customStylePrompt,
  setCustomStylePrompt,
  writingStyles,
  selectedModel,
  setSelectedModel,
  availableModels,
  tier,
  tierFeatures,
}: StandardOptionsProps) {
  const canUseCustomStyles = true; // Allow custom styles for all tiers
  const { toast } = useToast();

  // For ordering the models correctly
  const modelOrder = {
    // OpenAI models in desired order
    'o3-mini': 1,
    'gpt-4.5-preview': 2,
    'o1-mini': 3,
    'o1': 4, // o1 Pro
    'gpt-4o-mini': 5,
    'gpt-4o': 6,
    // Anthropic models in desired order
    'claude-3-7-sonnet-latest': 1,
    'claude-3-5-sonnet-20240620': 2,
    'claude-3-haiku-20240307': 3,
    'claude-3-opus-latest': 4,
    'claude-4-sonnet-latest': 5,
    'claude-4-opus-latest': 6,
  };

  // Group models by provider
  const openAiModels = availableModels.filter(model => !isAnthropicModel(model));
  const anthropicModels = availableModels.filter(model => isAnthropicModel(model));
  
  // Formatting helpers for model names
  const getModelDisplayName = (model: string) => {
    if (model === 'gpt-4o') return 'GPT-4o';
    if (model === 'gpt-4o-mini') return 'GPT-4o Mini';
    if (model === 'gpt-4.5-preview') return 'GPT-4.5 Preview';
    if (model === 'o3-mini') return 'o3 Mini';
    if (model === 'o1') return 'o1 Pro';
    if (model === 'o1-mini') return 'o1 Mini';
    
    if (model === 'claude-3-haiku-20240307') return 'Claude 3 Haiku';
    if (model === 'claude-3-5-sonnet-20240620') return 'Claude 3.5 Sonnet';
    if (model === 'claude-3-opus-latest') return 'Claude 3 Opus';
    if (model === 'claude-3-7-sonnet-latest') return 'Claude 3.7 Sonnet';
    if (model === 'claude-4-sonnet-latest') return 'Claude 4 Sonnet';
    if (model === 'claude-4-opus-latest') return 'Claude 4 Opus';
    
    return model;
  };
  
  // Get badge label based on model type
  const getModelBadgeLabel = (model: string) => {
    // OpenAI models
    if (model === 'gpt-4.5-preview') return 'Latest | High Performance | Advanced Reasoning';
    if (model === 'o3-mini') return 'Industry Leading | High Performance';
    if (model === 'o1-mini') return 'High Performance';
    if (model === 'o1') return 'Industry Leading | High Performance';
    if (model === 'gpt-4o-mini') return 'Standard Performance';
    if (model === 'gpt-4o') return 'Standard Performance';
    
    // Anthropic models
    if (model === 'claude-3-7-sonnet-latest') return 'Industry Leading | High Performance | Great Language Handling | Advanced Reasoning';
    if (model === 'claude-3-5-sonnet-20240620') return 'High Performance | Great Language Handling';
    if (model === 'claude-3-haiku-20240307') return 'Standard Performance';
    if (model === 'claude-3-opus-latest') return 'Standard Performance';
    if (model === 'claude-4-sonnet-latest') return 'Latest | Industry Leading | High Performance | Advanced Reasoning';
    if (model === 'claude-4-opus-latest') return 'Latest | Industry Leading | High Performance | Advanced Reasoning | Highest Quality';
    
    return '';
  };

  // Generate badge spans with appropriate colors
  const getModelBadges = (model: string) => {
    const badgeText = getModelBadgeLabel(model);
    if (!badgeText) return <></>;
    
    const badges = [];
    
    if (badgeText.includes('Latest')) {
      badges.push(<span key="latest" className="inline-block text-xs py-0.5 px-1.5 mb-1 mr-1 rounded-full bg-pink-100 text-pink-800">Latest</span>);
    }
    
    if (badgeText.includes('Industry Leading')) {
      badges.push(<span key="industry" className="inline-block text-xs py-0.5 px-1.5 mb-1 mr-1 rounded-full bg-blue-100 text-blue-800">Industry Leading</span>);
    }
    
    if (badgeText.includes('High Performance')) {
      badges.push(<span key="performance" className="inline-block text-xs py-0.5 px-1.5 mb-1 mr-1 rounded-full bg-green-100 text-green-800">High Performance</span>);
    }
    
    if (badgeText.includes('Great Language Handling')) {
      badges.push(<span key="language" className="inline-block text-xs py-0.5 px-1.5 mb-1 mr-1 rounded-full bg-orange-100 text-orange-800">Great Language Handling</span>);
    }
    
    if (badgeText.includes('Advanced Reasoning')) {
      badges.push(<span key="reasoning" className="inline-block text-xs py-0.5 px-1.5 mb-1 mr-1 rounded-full bg-purple-100 text-purple-800">Advanced Reasoning</span>);
    }
    
    if (badgeText.includes('Standard Performance')) {
      badges.push(<span key="standard" className="inline-block text-xs py-0.5 px-1.5 mb-1 mr-1 rounded-full bg-gray-100 text-gray-800">Standard Performance</span>);
    }
    
    if (badgeText.includes('Highest Quality')) {
      badges.push(<span key="highest" className="inline-block text-xs py-0.5 px-1.5 mb-1 mr-1 rounded-full bg-red-100 text-red-800">Highest Quality</span>);
    }
    
    return <div className="flex flex-wrap gap-1 mt-1 mb-1 w-full">{badges}</div>;
  };

  // Sort models according to desired order
  const sortedOpenAiModels = [...openAiModels].sort((a, b) => 
    (modelOrder[a as keyof typeof modelOrder] || 99) - (modelOrder[b as keyof typeof modelOrder] || 99)
  );
  
  const sortedAnthropicModels = [...anthropicModels].sort((a, b) => 
    (modelOrder[a as keyof typeof modelOrder] || 99) - (modelOrder[b as keyof typeof modelOrder] || 99)
  );

  // Initialize toggle states
  const [toggleStates, setToggleStates] = React.useState({
    introduction: false,
    longerParagraphs: false,
    conclusion: false,
    keyTakeaways: false,
  });

  // Effect to sync toggle states with form values
  React.useEffect(() => {
    setValue('includeIntroduction', toggleStates.introduction);
    setValue('longerParagraphs', toggleStates.longerParagraphs);
    setValue('includeConclusion', toggleStates.conclusion);
    setValue('includeKeyTakeaways', toggleStates.keyTakeaways);
  }, [toggleStates, setValue]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-base font-semibold text-gray-900 mb-3">
        Standard Options
      </div>
      <div className="space-y-6">
        <div>
          <Label className="text-sm">Content Type</Label>
          <Select defaultValue="guide" onValueChange={(value) => setValue('contentType', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select content type" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CONTENT_TYPES).map(([value, type]) => (
                <SelectItem key={value} value={value}>{type.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* AI Model Selection */}
        {availableModels && availableModels.length > 0 && (
          <div>
            <Label className="flex items-center gap-2 mb-2">
              AI Model
              {tier !== 'free' && (
                <span className="text-xs text-blue-800 font-medium">
                  {tier === 'pro' ? 'PRO' : 'ENTERPRISE'}
                </span>
              )}
            </Label>
            <div className="text-xs text-gray-500 mb-2">
              Available models for your {tier} tier:
            </div>
            <Select
              className="w-full max-w-[600px]"
              defaultValue={selectedModel} 
              onValueChange={(value) => {
                setSelectedModel(value);
                register('model').onChange({ target: { value } });
              }}
            >
              <SelectTrigger className="min-h-[3.5rem] py-4">
                <SelectValue placeholder="Select AI model" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px] w-full min-w-[600px]">
                {sortedOpenAiModels.length > 0 && (
                  <>
                    <SelectItem value="__openai_header" disabled className="font-semibold text-gray-500 border-b pb-1 mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500"></div>
                        OpenAI Models
                      </div>
                    </SelectItem>
                    {sortedOpenAiModels.map((model) => (
                      <SelectItem key={model} value={model} className="pl-6">
                        <div className="flex flex-col w-full relative pr-2">
                          <div className="flex items-center gap-2 min-h-[2rem]">
                            <span className="font-medium text-sm">{getModelDisplayName(model)}</span>
                            {tier === 'free' && !SERVICE_TIERS.free.availableModels.includes(model) && (
                              <span className="text-[10px] font-semibold text-blue-800 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                {model === 'claude-3-7-sonnet-latest' || model.includes('claude-4') ? 'ENTERPRISE' : 'PRO+'}
                              </span>
                            )}
                          </div>
                          {getModelBadges(model)}
                        </div>
                      </SelectItem>
                    ))}
                  </>
                )}
                
                {sortedAnthropicModels.length > 0 && (
                  <>
                    <SelectItem value="__anthropic_header" disabled className="font-semibold text-gray-500 border-b pb-1 mb-1 mt-2">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                        Anthropic Claude Models
                      </div>
                    </SelectItem>
                    {sortedAnthropicModels.map((model) => (
                      <SelectItem key={model} value={model} className="pl-6">
                        <div className="flex flex-col w-full relative pr-2">
                          <div className="flex items-center gap-2 min-h-[2rem]">
                            <span className="font-medium text-sm">{getModelDisplayName(model)}</span>
                            {tier === 'free' && !SERVICE_TIERS.free.availableModels.includes(model) && (
                              <span className="text-[10px] font-semibold text-blue-800 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                {model === 'claude-3-7-sonnet-latest' || model.includes('claude-4') ? 'ENTERPRISE' : 'PRO+'}
                              </span>
                            )}
                          </div>
                          {getModelBadges(model)}
                        </div>
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <Toggle
                pressed={toggleStates.introduction}
                onPressedChange={(pressed) => setToggleStates(prev => ({ ...prev, introduction: pressed }))}
                className="group bg-white border border-gray-200 hover:bg-gray-50 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-800 data-[state=on]:border-blue-200 relative"
              >
                <span>Introduction</span>
                <div className="absolute invisible group-hover:visible bg-black text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2 w-48 text-center">
                  Adds a compelling introduction section that sets up the topic and engages readers
                </div>
              </Toggle>
              <Toggle
                pressed={toggleStates.longerParagraphs}
                onPressedChange={(pressed) => setToggleStates(prev => ({ ...prev, longerParagraphs: pressed }))}
                className="group bg-white border border-gray-200 hover:bg-gray-50 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-800 data-[state=on]:border-blue-200 relative animate-pulse shadow-[0_0_15px_rgba(59,130,246,0.5)]"
              >
                <span>Longer Paragraphs</span>
                <div className="absolute invisible group-hover:visible bg-black text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2 w-48 text-center">
                  Highly recommended to be enabled to prevent low effort responses. Creates more detailed, in-depth paragraphs that provide comprehensive coverage of each point.
                </div>
              </Toggle>
              <Toggle
                pressed={toggleStates.conclusion}
                onPressedChange={(pressed) => setToggleStates(prev => ({ ...prev, conclusion: pressed }))}
                className="group bg-white border border-gray-200 hover:bg-gray-50 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-800 data-[state=on]:border-blue-200 relative"
              >
                <span>Conclusion</span>
                <div className="absolute invisible group-hover:visible bg-black text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2 w-48 text-center">
                  Adds a summary conclusion that ties together the main points
                </div>
              </Toggle>
              <Toggle
                pressed={toggleStates.keyTakeaways}
                onPressedChange={(pressed) => setToggleStates(prev => ({ ...prev, keyTakeaways: pressed }))}
                className="group bg-white border border-gray-200 hover:bg-gray-50 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-800 data-[state=on]:border-blue-200 relative"
              >
                <span>Key Takeaways</span>
                <div className="absolute invisible group-hover:visible bg-black text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2 w-48 text-center">
                  Includes a bulleted list of key points for quick reference
                </div>
              </Toggle>
            </div>
          </div>
        )}

        <div>
          <Label className="text-sm">Blog Title</Label>
          <Input
            className="w-full"
            {...register('title', { required: 'Title is required' })}
            placeholder="Enter your blog title"
          />
          {errors.title && (
            <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>
          )}
        </div>

        <div>
          <Label className="text-sm">Keywords</Label>
          <Input
            className="w-full"
            {...register('keywords', { required: 'Keywords are required' })}
            placeholder="Enter keywords, separated by commas"
          />
          {errors.keywords && (
            <p className="mt-1 text-xs text-red-600">{errors.keywords.message}</p>
          )}
        </div>

        <div>
          <Label className="text-sm">Context</Label>
          <textarea
            {...register('context')}
            className="w-full min-h-[100px] p-2 border rounded-md"
            placeholder="Add any additional context or specific requirements for the content..."
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Optional: Provide background information, specific angles, or requirements that should be considered.
          </p>
        </div>

        <div>
          <Label className="text-sm">Industry</Label>
          <Input
            className="w-full"
            {...register('industry', { required: 'Industry is required' })}
            placeholder="e.g., Technology, Healthcare, Finance"
          />
        </div>

        <div>
          <Label className="text-sm">Tone</Label>
          <Select defaultValue="professional" onValueChange={(value) => setValue('tone', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a tone" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TONES).map(([value, description]) => (
                <SelectItem key={value} value={value} className="text-sm">{description}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-sm">Content Framework</Label>
          <Select defaultValue="none" onValueChange={(value) => register('framework').onChange({ target: { value } })}>
            <SelectTrigger>
              <SelectValue placeholder="Select a framework" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" className="text-sm">None</SelectItem>
              {Object.entries(FRAMEWORKS).map(([key, description]) => (
                <SelectItem key={key} value={key} className="text-sm">{key} - {description}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-sm">Writing Style</Label>
          <Select defaultValue="default" onValueChange={handleWritingStyleChange}>
            <SelectTrigger disabled={!canUseCustomStyles}>
              <SelectValue placeholder="Select a writing style" />
            </SelectTrigger>
            <SelectContent className={!canUseCustomStyles ? 'opacity-50 pointer-events-none' : ''}>
              <SelectItem value="default" className="text-sm">Default Style</SelectItem>
              <SelectItem value="custom" className="border-b border-border mb-2 pb-2">
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Custom Style</span>
                </div>
              </SelectItem>
              {WRITING_STYLES.map((style) => (
                <SelectItem key={style.name} value={style.name} className="py-2 text-sm">
                  {style.name} - {style.description}
                </SelectItem>
              ))}
              {writingStyles?.map((style) => (
                <SelectItem key={style._id} value={style._id} className="py-2 text-sm">
                  {style.name} - {style.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {writingStyleState === 'custom' && (
            <div className="mt-2">
              <textarea
                value={customStylePrompt}
                onChange={(e) => {
                  setCustomStylePrompt(e.target.value);
                  register('writingStyleId').onChange({ target: { value: e.target.value } });
                }}
                className="w-full min-h-[100px] p-2 border rounded-md text-sm"
                placeholder="Describe how you want the content to be written..."
              />
            </div>
          )}
        </div>

        <div>
          <Label className="text-sm">Readability Level</Label>
          <Select defaultValue="grade8" onValueChange={(value) => register('readabilityLevel').onChange({ target: { value } })}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select readability level" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(READABILITY_LEVELS).map(([value, description]) => (
                <SelectItem key={value} value={value} className="text-sm">{description}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="enableSectionLabeling"
            {...register('enableSectionLabeling')}
            defaultChecked={true}
            onCheckedChange={(checked) => 
              setValue('enableSectionLabeling', checked as boolean)
            }
          />
          <Label htmlFor="enableSectionLabeling" className="text-sm">
            Enable Section Labeling
          </Label>
        </div>

        <div>
          <Label className="text-sm">Word Count</Label>
          <Input
            type="number"
            {...register('wordCount', { 
              required: 'Word count is required',
              min: { value: 300, message: 'Minimum 300 words' },
              max: { value: tierFeatures.features.maxWordCount, message: `Maximum ${tierFeatures.features.maxWordCount} words` },
              valueAsNumber: true
            })}
            placeholder="e.g., 1000"
          />
          {errors.wordCount && (
            <p className="mt-1 text-xs text-red-600">{errors.wordCount.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}